import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, Save, Search, Download, RefreshCcw, X, FileText, Printer,
  Mail, MessageCircle, StickyNote, Undo2, Zap, Eye, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";
import { fmtDateTime } from "../../lib/ist";

const STATUS_COLORS = {
  successful: "bg-emerald-500/15 text-emerald-300",
  pending_payment: "bg-amber-500/15 text-amber-300",
  payment_processing: "bg-blue-500/15 text-blue-300",
  webhook_verification_pending: "bg-blue-500/15 text-blue-300",
  failed: "bg-rose-500/15 text-rose-300",
  cancelled: "bg-rose-500/15 text-rose-300",
  expired: "bg-slate-500/15 text-slate-300",
  refunded: "bg-purple-500/15 text-purple-300",
  partially_refunded: "bg-purple-500/15 text-purple-300",
  verification_failed: "bg-rose-500/15 text-rose-300",
};
const Badge = ({ s }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[s] || "bg-slate-500/15 text-slate-300"}`}>
    {(s || "").replace(/_/g, " ")}
  </span>
);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const Toggle = ({ label, value, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!value)} data-testid={testid}
    className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg bg-slate-800/60 border border-slate-700 hover:border-slate-500 transition-colors">
    <span className="text-sm text-slate-300">{label}</span>
    <span className={`w-9 h-5 rounded-full relative transition-colors ${value ? "bg-emerald-500" : "bg-slate-600"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-[18px]" : "left-0.5"}`}/>
    </span>
  </button>
);

const NumIn = ({ label, value, onChange, testid, step = "1" }) => (
  <label className="block">
    <span className="text-xs text-slate-400">{label}</span>
    <input type="number" step={step} value={value ?? ""} onChange={(e)=>onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid={testid}/>
  </label>
);
const TxtIn = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs text-slate-400">{label}</span>
    <input value={value ?? ""} onChange={(e)=>onChange(e.target.value)}
      className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid={testid}/>
  </label>
);

/* ───────────────────────────── Settings tab ───────────────────────────── */

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { api.get("/admin/sm888/settings").then(r=>setS(r.data)).catch(()=>toast.error("Failed to load settings")); }, []);
  if (!s) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-blue-400"/></div>;

  const up = (patch) => setS(prev => ({ ...prev, ...patch }));
  const upNest = (key, patch) => setS(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const upPlan = (i, patch) => setS(prev => {
    const plans = prev.plans.map((p, idx) => idx === i ? { ...p, ...patch } : p);
    return { ...prev, plans };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/sm888/settings", s);
      setS(data); toast.success("SM888 settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const testGw = async (gateway) => {
    setTestResult({ gateway, loading: true });
    try {
      const { data } = await api.post("/admin/sm888/test-gateway", { gateway });
      setTestResult(data);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch (e) { setTestResult({ ok:false, gateway, message:"Request failed" }); toast.error("Test failed"); }
  };

  return (
    <div className="space-y-5" data-testid="sm888-settings-tab">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">Link controls</h3>
          <div className="space-y-2">
            <Toggle label="SM888 payment link enabled" value={s.enabled} onChange={(v)=>up({enabled:v})} testid="sm888-set-enabled"/>
            <Toggle label="Show 'open on phone' QR hint to desktop users" value={s.mobile_only} onChange={(v)=>up({mobile_only:v})} testid="sm888-set-mobile-only"/>
            <Toggle label="Payment collection" value={s.collect_payments} onChange={(v)=>up({collect_payments:v})} testid="sm888-set-collect"/>
            <Toggle label="Auto invoice generation" value={s.auto_invoice} onChange={(v)=>up({auto_invoice:v})} testid="sm888-set-auto-invoice"/>
            <Toggle label="Auto receipt generation" value={s.auto_receipt} onChange={(v)=>up({auto_receipt:v})} testid="sm888-set-auto-receipt"/>
            <Toggle label="Auto receipt download" value={s.auto_download} onChange={(v)=>up({auto_download:v})} testid="sm888-set-auto-download"/>
            <Toggle label="Receipt sharing" value={s.share_enabled} onChange={(v)=>up({share_enabled:v})} testid="sm888-set-share"/>
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">Payment gateway</h3>
          <div className="flex gap-2 mb-3">
            {["razorpay","payglocal"].map(g => (
              <button key={g} onClick={()=>up({active_gateway:g})}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold border transition-colors ${s.active_gateway===g ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}
                data-testid={`sm888-gw-${g}`}>
                {g === "razorpay" ? "Razorpay" : "PayGlocal"}{s.active_gateway===g && " · default"}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <Toggle label="Razorpay configured/available" value={s.razorpay_enabled} onChange={(v)=>up({razorpay_enabled:v})} testid="sm888-set-rzp-enabled"/>
            <Toggle label="PayGlocal configured/available" value={s.payglocal_enabled} onChange={(v)=>up({payglocal_enabled:v})} testid="sm888-set-pg-enabled"/>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={()=>testGw("razorpay")} className="flex-1 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold inline-flex items-center justify-center gap-1.5" data-testid="sm888-test-rzp"><Zap size={12}/> Test Razorpay</button>
            <button onClick={()=>testGw("payglocal")} className="flex-1 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold inline-flex items-center justify-center gap-1.5" data-testid="sm888-test-pg"><Zap size={12}/> Test PayGlocal</button>
          </div>
          {testResult && !testResult.loading && (
            <div className={`mt-2 text-xs px-3 py-2 rounded-lg ${testResult.ok ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`} data-testid="sm888-test-result">
              {testResult.message}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <NumIn label="Min amount (₹)" value={s.min_amount} onChange={(v)=>up({min_amount:v})} testid="sm888-set-min"/>
            <NumIn label="Max amount (₹)" value={s.max_amount} onChange={(v)=>up({max_amount:v})} testid="sm888-set-max"/>
            <NumIn label="Enterprise ≥ (₹)" value={s.enterprise_threshold} onChange={(v)=>up({enterprise_threshold:v})} testid="sm888-set-ent"/>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="text-white font-semibold mb-3">Plans (auto-selected by amount)</h3>
        <div className="space-y-2">
          {s.plans.map((p, i) => (
            <div key={p.key} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><TxtIn label="Plan name" value={p.name} onChange={(v)=>upPlan(i,{name:v})} testid={`sm888-plan-name-${i}`}/></div>
              <div className="col-span-3"><NumIn label="Min (₹)" value={p.min} onChange={(v)=>upPlan(i,{min:v})} testid={`sm888-plan-min-${i}`}/></div>
              <div className="col-span-3"><NumIn label="Max (₹)" value={p.max} onChange={(v)=>upPlan(i,{max:v})} testid={`sm888-plan-max-${i}`}/></div>
              <div className="col-span-2">
                <Toggle label={p.enabled ? "On" : "Off"} value={p.enabled} onChange={(v)=>upPlan(i,{enabled:v})} testid={`sm888-plan-enabled-${i}`}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">GST / tax settings</h3>
          <div className="space-y-2">
            <Toggle label="GST enabled" value={s.tax.gst_enabled} onChange={(v)=>upNest("tax",{gst_enabled:v})} testid="sm888-set-gst"/>
            {s.tax.gst_enabled && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <TxtIn label="GSTIN" value={s.tax.gst_number} onChange={(v)=>upNest("tax",{gst_number:v})} testid="sm888-set-gstin"/>
                <NumIn label="Rate %" value={s.tax.rate} onChange={(v)=>upNest("tax",{rate:v})} testid="sm888-set-gst-rate"/>
                <label className="block col-span-2">
                  <span className="text-xs text-slate-400">Tax mode</span>
                  <select value={s.tax.mode} onChange={(e)=>upNest("tax",{mode:e.target.value})}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-set-gst-mode">
                    <option value="igst">IGST (inter-state)</option>
                    <option value="cgst_sgst">CGST + SGST (intra-state)</option>
                  </select>
                </label>
              </div>
            )}
          </div>
          <h3 className="text-white font-semibold mt-5 mb-3">Numbering</h3>
          <div className="grid grid-cols-2 gap-2">
            <TxtIn label="Invoice prefix" value={s.numbering.invoice_prefix} onChange={(v)=>upNest("numbering",{invoice_prefix:v})} testid="sm888-set-inv-prefix"/>
            <NumIn label="Next invoice #" value={s.numbering.invoice_next} onChange={(v)=>upNest("numbering",{invoice_next:v})} testid="sm888-set-inv-next"/>
            <TxtIn label="Receipt prefix" value={s.numbering.receipt_prefix} onChange={(v)=>upNest("numbering",{receipt_prefix:v})} testid="sm888-set-rcp-prefix"/>
            <NumIn label="Next receipt #" value={s.numbering.receipt_next} onChange={(v)=>upNest("numbering",{receipt_next:v})} testid="sm888-set-rcp-next"/>
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">Business details (invoices &amp; receipts)</h3>
          <div className="grid grid-cols-2 gap-2">
            <TxtIn label="Brand name" value={s.business.name} onChange={(v)=>upNest("business",{name:v})} testid="sm888-set-biz-name"/>
            <TxtIn label="Legal line" value={s.business.legal} onChange={(v)=>upNest("business",{legal:v})} testid="sm888-set-biz-legal"/>
            <div className="col-span-2"><TxtIn label="Business address" value={s.business.address} onChange={(v)=>upNest("business",{address:v})} testid="sm888-set-biz-address"/></div>
            <TxtIn label="Support phone" value={s.business.phone} onChange={(v)=>upNest("business",{phone:v})} testid="sm888-set-biz-phone"/>
            <TxtIn label="Support WhatsApp" value={s.business.whatsapp} onChange={(v)=>upNest("business",{whatsapp:v})} testid="sm888-set-biz-wa"/>
            <div className="col-span-2"><TxtIn label="Support email" value={s.business.email} onChange={(v)=>upNest("business",{email:v})} testid="sm888-set-biz-email"/></div>
          </div>
          <h3 className="text-white font-semibold mt-5 mb-3">Notifications</h3>
          <div className="space-y-2">
            <Toggle label="Email customer on payment" value={s.notifications.email_customer} onChange={(v)=>upNest("notifications",{email_customer:v})} testid="sm888-set-notif-cust"/>
            <Toggle label="Email admin on payment" value={s.notifications.email_admin} onChange={(v)=>upNest("notifications",{email_admin:v})} testid="sm888-set-notif-admin"/>
          </div>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving}
          className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold inline-flex items-center gap-2 shadow-lg"
          data-testid="sm888-save-settings">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save settings
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Transactions tab ─────────────────────────── */

function TxDetailModal({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refund, setRefund] = useState({ amount: "", reason: "", manual: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/sm888/transactions/${id}`).then(r=>setData(r.data)).catch(()=>toast.error("Failed to load"));
  }, [id]);
  useEffect(load, [load]);

  const o = data?.order;
  const receiptUrl = `${API}/sm888/orders/${id}/receipt.pdf`;
  const invoiceUrl = `${API}/sm888/orders/${id}/invoice.pdf`;

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/sm888/transactions/${id}/notes`, { text: note }); setNote(""); load(); toast.success("Note added"); }
    catch { toast.error("Failed"); }
  };
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/admin/sm888/transactions/${id}/resend-email`); toast.success("Emails re-sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doRefund = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/sm888/transactions/${id}/refund`, {
        amount: refund.amount ? Number(refund.amount) : null,
        reason: refund.reason || null, manual: refund.manual,
      });
      toast.success("Refund recorded"); setRefundOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };

  const c = o?.customer || {};
  const waText = o ? encodeURIComponent(`Optivize payment ${o.order_number}: ${inr(o.total_amount)} · ${o.plan_name} · ${o.status}. Receipt: ${receiptUrl}`) : "";

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl p-6 my-6" onClick={(e)=>e.stopPropagation()} data-testid="sm888-tx-modal">
          {!o ? <Loader2 className="animate-spin text-blue-400 mx-auto my-10"/> : (<>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-white font-bold text-lg font-mono">{o.order_number}</div>
                <div className="mt-1"><Badge s={o.status}/></div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="sm888-tx-close"><X size={20}/></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-800/50 rounded-xl p-4 space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Customer</div>
                <div className="text-white font-semibold">{c.name}</div>
                <div className="text-slate-400">{c.phone} · {c.email}</div>
                <div className="text-slate-400">{c.address}, {c.city}, {c.state} — {c.postal_code}, {c.country}</div>
                <div className="text-slate-500 text-xs pt-1">IP {o.ip || "—"} · {(o.user_agent || "").slice(0, 60)}</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Payment</div>
                <div className="text-white font-semibold">{o.plan_name} · {inr(o.total_amount)}{o.tax_amount ? ` (incl tax ${inr(o.tax_amount)})` : ""}</div>
                <div className="text-slate-400">Gateway: {o.gateway || "—"} · TXN: <span className="font-mono">{o.gateway_txn_id || "—"}</span></div>
                <div className="text-slate-400">Invoice: {o.invoice_number || "—"} · Receipt: {o.receipt_number || "—"}</div>
                <div className="text-slate-500 text-xs">Created {fmtDateTime(o.created_at)} · Paid {o.paid_at ? fmtDateTime(o.paid_at) : "—"}</div>
              </div>
            </div>

            {o.refunds?.length > 0 && (
              <div className="mt-3 bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 text-xs text-purple-200">
                <b>Refunds:</b> {o.refunds.map(r => `${inr(r.amount)} by ${r.by} (${r.mode || "manual"}) ${fmtDateTime(r.at)}`).join(" · ")}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <a href={invoiceUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="sm888-tx-invoice"><FileText size={13}/> Invoice PDF</a>
              <a href={receiptUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="sm888-tx-receipt"><Printer size={13}/> Receipt PDF</a>
              <button onClick={resend} disabled={busy || o.status !== "successful"} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="sm888-tx-resend"><Mail size={13}/> Resend email</button>
              <a href={`https://wa.me/${(c.phone||"").replace(/\D/g,"") ? "91"+(c.phone||"").replace(/\D/g,"") : ""}?text=${waText}`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="sm888-tx-wa"><MessageCircle size={13}/> WhatsApp</a>
              {["successful","partially_refunded"].includes(o.status) && (
                <button onClick={()=>setRefundOpen(v=>!v)} className="px-3 py-2 rounded-lg bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="sm888-tx-refund-toggle"><Undo2 size={13}/> Refund</button>
              )}
            </div>

            {refundOpen && (
              <div className="mt-3 bg-slate-800/60 border border-purple-500/30 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-2">
                  <NumIn label={`Amount (blank = full ${inr(o.total_amount)})`} value={refund.amount} onChange={(v)=>setRefund(r=>({...r, amount:v}))} step="0.01" testid="sm888-refund-amount"/>
                  <TxtIn label="Reason" value={refund.reason} onChange={(v)=>setRefund(r=>({...r, reason:v}))} testid="sm888-refund-reason"/>
                </div>
                <div className="mt-2"><Toggle label="Manual refund note only (no gateway API call)" value={refund.manual} onChange={(v)=>setRefund(r=>({...r, manual:v}))} testid="sm888-refund-manual"/></div>
                <button onClick={doRefund} disabled={busy} className="mt-3 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-bold" data-testid="sm888-refund-submit">
                  {busy ? "Processing…" : "Initiate refund"}
                </button>
              </div>
            )}

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Internal notes</div>
              {(o.notes || []).map(n => (
                <div key={n.id} className="text-xs text-slate-300 bg-slate-800/40 rounded-lg px-3 py-2 mb-1.5">
                  <span className="text-slate-500">{fmtDateTime(n.at)} · {n.by}:</span> {n.text}
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Add internal note…"
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-note-input"/>
                <button onClick={addNote} className="px-3 py-2 rounded-lg bg-slate-700 text-white text-xs font-bold inline-flex items-center gap-1" data-testid="sm888-note-add"><StickyNote size={13}/> Add</button>
              </div>
            </div>

            {data.webhook_events?.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Webhook events</div>
                {data.webhook_events.map(ev => (
                  <div key={ev.id} className="text-xs text-slate-400 font-mono px-3 py-1.5 bg-slate-800/40 rounded-lg mb-1">
                    {fmtDateTime(ev.received_at)} · {ev.gateway} · {ev.event} · {ev.txn_id || "—"}
                  </div>
                ))}
              </div>
            )}
          </>)}
        </div>
      </div>
    </ModalPortal>
  );
}

function TransactionsTab() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ total: 0, paid_total: 0 });
  const [filters, setFilters] = useState({ q:"", status:"", gateway:"", from_date:"", to_date:"" });
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([,v]) => v));
    try {
      const { data } = await api.get("/admin/sm888/transactions", { params });
      setRows(data.rows); setMeta({ total: data.total, paid_total: data.paid_total });
    } catch { toast.error("Failed to load transactions"); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)));
      const res = await api.get(`/admin/sm888/transactions/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "sm888-transactions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const setF = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="sm888-transactions-tab">
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input value={filters.q} onChange={setF("q")} placeholder="Search name / email / txn / invoice…"
            className="pl-9 pr-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm w-64" data-testid="sm888-tx-search"/>
        </div>
        <select value={filters.status} onChange={setF("status")} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-tx-status-filter">
          <option value="">All statuses</option>
          {["successful","pending_payment","payment_processing","failed","cancelled","expired","refunded","partially_refunded","webhook_verification_pending","verification_failed"].map(s=>(
            <option key={s} value={s}>{s.replace(/_/g," ")}</option>
          ))}
        </select>
        <select value={filters.gateway} onChange={setF("gateway")} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-tx-gateway-filter">
          <option value="">All gateways</option>
          <option value="razorpay">Razorpay</option>
          <option value="payglocal">PayGlocal</option>
        </select>
        <input type="date" value={filters.from_date} onChange={setF("from_date")} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-tx-from"/>
        <input type="date" value={filters.to_date} onChange={setF("to_date")} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm" data-testid="sm888-tx-to"/>
        <button onClick={load} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-1.5" data-testid="sm888-tx-refresh"><RefreshCcw size={13}/></button>
        <button onClick={exportCsv} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="sm888-tx-export"><Download size={13}/> Export CSV</button>
        <div className="ml-auto text-xs text-slate-400">
          {meta.total} records · Collected <span className="text-emerald-400 font-bold">{inr(meta.paid_total)}</span>
        </div>
      </div>

      {!rows ? <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-blue-400"/></div> :
        rows.length === 0 ? <p className="text-slate-500 text-sm py-10 text-center">No transactions yet. Share https://optivize.digital/SM888 with a customer.</p> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-2.5 px-3 text-left">Date (IST)</th>
                <th className="text-left px-2">Order</th>
                <th className="text-left px-2">Customer</th>
                <th className="text-left px-2">Plan</th>
                <th className="text-right px-2">Amount</th>
                <th className="text-left px-2">Gateway</th>
                <th className="text-left px-2">Status</th>
                <th className="text-right px-3">View</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(o => (
                <tr key={o.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2.5 px-3 text-slate-400 whitespace-nowrap">{fmtDateTime(o.created_at)}</td>
                  <td className="px-2 font-mono text-cyan-300 text-xs">{o.order_number}</td>
                  <td className="px-2">
                    <div className="text-white">{o.customer?.name}</div>
                    <div className="text-slate-500 text-xs">{o.customer?.email}</div>
                  </td>
                  <td className="px-2 text-slate-300">{o.plan_name}</td>
                  <td className="px-2 text-right text-white font-semibold">{inr(o.total_amount)}</td>
                  <td className="px-2 text-slate-400 capitalize">{o.gateway || "—"}</td>
                  <td className="px-2"><Badge s={o.status}/></td>
                  <td className="px-3 text-right">
                    <button onClick={()=>setDetail(o.id)} className="text-blue-400 hover:text-blue-300" data-testid={`sm888-tx-view-${o.id.slice(0,8)}`}><Eye size={16}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && <TxDetailModal id={detail} onClose={()=>setDetail(null)} onChanged={load}/>}
    </div>
  );
}

/* ─────────────────────────── Webhook log tab ─────────────────────────── */

function WebhooksTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/sm888/webhook-events").then(r=>setRows(r.data.rows)).catch(()=>setRows([])); }, []);
  if (!rows) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-blue-400"/></div>;
  return (
    <div className="card overflow-x-auto" data-testid="sm888-webhooks-tab">
      {rows.length === 0 ? <p className="text-slate-500 text-sm p-8 text-center">No webhook events received yet.</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
            <tr><th className="py-2.5 px-3 text-left">Received (IST)</th><th className="text-left px-2">Gateway</th><th className="text-left px-2">Event</th><th className="text-left px-2">Order</th><th className="text-left px-2">Txn</th></tr>
          </thead>
          <tbody>
            {rows.map(ev => (
              <tr key={ev.id} className="border-b border-slate-800">
                <td className="py-2 px-3 text-slate-400 whitespace-nowrap">{fmtDateTime(ev.received_at)}</td>
                <td className="px-2 text-slate-300 capitalize">{ev.gateway}</td>
                <td className="px-2 font-mono text-xs text-cyan-300">{ev.event}</td>
                <td className="px-2 font-mono text-xs text-slate-400">{(ev.order_id || "").slice(0,8)}</td>
                <td className="px-2 font-mono text-xs text-slate-400">{ev.txn_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─────────────────────────────── Root ─────────────────────────────── */

export default function AdminSM888() {
  const [tab, setTab] = useState("transactions");
  return (
    <div className="space-y-5" data-testid="admin-sm888">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tag mb-2 inline-flex items-center gap-1.5"><Smartphone size={12}/> Payments · SM888</div>
          <h1 className="text-2xl font-extrabold text-white" style={{fontFamily:"Outfit"}}>SM888 Payment Link</h1>
          <p className="text-slate-400 text-sm">Mobile-only secure payment link at <span className="font-mono text-cyan-300">optivize.digital/SM888</span></p>
        </div>
        <div className="flex gap-2">
          {[["transactions","Transactions"],["settings","Settings"],["webhooks","Webhook Log"]].map(([id,label]) => (
            <button key={id} onClick={()=>setTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition-colors ${tab===id ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"}`}
              data-testid={`sm888-tab-${id}`}>{label}</button>
          ))}
        </div>
      </div>
      {tab === "settings" && <SettingsTab/>}
      {tab === "transactions" && <TransactionsTab/>}
      {tab === "webhooks" && <WebhooksTab/>}
    </div>
  );
}
